import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import { hreflangLinks } from "@/lib/seo";

const URL_FI = "https://montessori24.fi/koulutus";
const TITLE = "Koulutus & Montessori-opas | Montessori24";
const DESC =
  "Montessori kotona — käytännön opas vanhemmille. Periaatteet, ikäryhmittäiset aktiviteetit 0–6 v, oppimistornin ja Pikler-kolmion käyttöohjeet, arjen rutiinit ja ladattava aktiviteettiopas.";

export const Route = createFileRoute("/koulutus")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "montessori kotona, pikler aktiviteetit, oppimistorni käyttö, montessori vauva, montessori taapero, kotimontessori" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_FI },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:site_name", content: "Montessori24" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL_FI }, ...hreflangLinks(URL_FI)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: URL_FI,
          inLanguage: "fi-FI",
        }),
      },
    ],
  }),
  component: KoulutusPage,
});

type AgeKey = "0-1" | "1-3" | "3-6";
type Activity = { title: string; materials: string; benefit: string };

const ACTIVITIES: Record<AgeKey, ReadonlyArray<Activity>> = {
  "0-1": [
    { title: "Kontrastikortit lattiapatjalla", materials: "Musta-valko-kuvakortit, lattiapatja", benefit: "Näön kehitys ja rauhallinen tarkkailu 0–4 kk." },
    { title: "Tarttumislelu kaaresta roikkuen", materials: "Pikler-kaari + puinen rengas nauhassa", benefit: "Käden ja silmän yhteistyö, ensimmäiset tarttumiskokemukset." },
    { title: "Vapaa liike lattialla", materials: "Iso lattiapatja tai matto", benefit: "Selän voima, kääntyminen, ryömiminen omassa tahdissa." },
    { title: "Peili turvallisella korkeudella", materials: "Kiinteä peili lattiatasossa", benefit: "Oman kehon havainnointi ja motivaatio nostaa päätä." },
    { title: "Kori 'aarteita'", materials: "Puisia esineitä, pieniä palloja korissa", benefit: "Aisti- ja tarttumisleikki 6–12 kk." },
  ],
  "1-3": [
    { title: "Vihannesten pesu oppimistornissa", materials: "Oppimistorni, kulho, porkkanoita", benefit: "Osallistuminen aikuisten työhön, käsivoima ja itsetunto." },
    { title: "Vedenkaato pienestä kannusta", materials: "Kaksi pientä kannua, tarjotin, sieni", benefit: "Keskittyminen, tarkkuus, korjaamisen taito." },
    { title: "Pukemisharjoituksia (napit, tarrat)", materials: "Pukemiskehys tai vanhoja vaatteita", benefit: "Sormien hienomotoriikka ja itsenäisyys." },
    { title: "Kiipeily Pikler-kolmiossa", materials: "Pikler-kolmio + ramppi", benefit: "Karkeamotoriikka, tasapaino, riskin arviointi." },
    { title: "Ruoan tarjoilu lautaselle", materials: "Pieni kauha, kulho, lautanen", benefit: "Silmä-käsi-koordinaatio ja huolellisuus." },
  ],
  "3-6": [
    { title: "Voileivän valmistus alusta loppuun", materials: "Oppimistorni, veitsi (lasten), leipä", benefit: "Työn ketju, järjestys ja itsenäisyys." },
    { title: "Kirjainten piirto hiekkaan", materials: "Hiekkatarjotin, sormi", benefit: "Kirjoittamisen esiharjoitus aistikanavan kautta." },
    { title: "Lajittelu väreittäin / koon mukaan", materials: "Palikoita tai luonnonesineitä", benefit: "Matemaattinen ajattelu, luokittelu." },
    { title: "Pyykin viikkaus ja vaaterekki", materials: "Lasten vaaterekki, ripustimet", benefit: "Vastuu omista tavaroista, järjestys." },
    { title: "Kasvin hoito ja kastelu", materials: "Ruukkukukka, pieni kannu", benefit: "Elämän kunnioitus, rutiini, pitkäjänteisyys." },
  ],
};

const AGE_META: Record<AgeKey, { label: string; sub: string }> = {
  "0-1": { label: "0–12 kk", sub: "Vauva — vapaa liike ja aistit" },
  "1-3": { label: "1–3 v", sub: "Taapero — itsenäisyys ja osallistuminen" },
  "3-6": { label: "3–6 v", sub: "Esikouluikäinen — työ ja keskittyminen" },
};

function KoulutusPage() {
  const [age, setAge] = useState<AgeKey>("1-3");
  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-20 md:py-28">
          <div className="grid gap-10 md:grid-cols-[1.3fr_1fr] md:gap-16 md:items-end">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-clay">Koulutus & opas</span>
              <h1 className="mt-6 font-display text-5xl leading-[1.05] text-foreground md:text-7xl text-balance">
                Montessori kotona — käytännön opas vanhemmille
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Periaatteet lyhyesti, konkreettiset aktiviteetit vauvasta esikouluun ja käyttöohjeet oppimistorniin ja Pikler-kolmioon. Ei jargonia — vain toimivia arjen ideoita.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#aktiviteetit" className="inline-flex items-center rounded-full bg-foreground px-5 py-2.5 text-sm uppercase tracking-[0.18em] text-background hover:bg-foreground/85">
                  Aktiviteetit ikäryhmittäin
                </a>
              </div>
            </div>
            <aside className="border-l-2 border-clay pl-6">
              <blockquote className="font-display text-2xl leading-snug text-foreground md:text-3xl">
                "Any unnecessary help is an obstacle to development."
              </blockquote>
              <figcaption className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                Maria Montessori
              </figcaption>
            </aside>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Periaatteet</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              Montessori & Pikler — kuusi ajatusta arjen pohjaksi
            </h2>
          </div>
          <ol className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-3">
            {[
              { n: "01", t: "Valmisteltu ympäristö", d: "Kaikki lapsen mittakaavassa — hylly, koukku, tuoli. Itsenäisyys alkaa siitä, että hän ylettää itse." },
              { n: "02", t: "Vapaa liike", d: "Vauvaa ei istuteta eikä kävelytetä. Lattia + tila = kaikki mitä motoriikka tarvitsee." },
              { n: "03", t: "Vapaus rajojen sisällä", d: "Vaihtoehtoja on rajallinen määrä, ja kaikki ovat hyviä. Lapsi valitsee — aikuinen ei komennella tarvitse." },
              { n: "04", t: "Työ on leikkiä", d: "Tiskaus, pyyhkiminen ja pukeminen ovat aidoimpia oppimishetkiä. Oppimistorni tuo lapsen mukaan." },
              { n: "05", t: "Kunnioittava kieli", d: "Kerro etukäteen mitä teet. Vauva ymmärtää sävyn ennen sanoja — Piklerin ydinperintö." },
              { n: "06", t: "Aikuinen tarkkailee", d: "Ensin katso, sitten auta — ja vain sen verran kuin tarvitaan. Lopeta apu heti, kun lapsi pärjää itse." },
            ].map((s) => (
              <li key={s.n} className="bg-background p-8 md:p-10">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-4xl text-clay">{s.n}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <h3 className="mt-5 font-display text-xl text-foreground text-balance md:text-2xl">{s.t}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section id="aktiviteetit" className="border-b border-border bg-cream scroll-mt-20">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-8 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Aktiviteetit ikäryhmittäin</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              Valitse ikä ja aloita heti
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Jokainen harjoitus vie 5–20 minuuttia ja käyttää tavaraa, joka löytyy jo kotoa tai on saatavilla mallistostamme.
            </p>
          </div>

          <div className="mb-8 inline-flex rounded-full border border-border bg-background p-1">
            {(Object.keys(AGE_META) as AgeKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setAge(k)}
                className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors md:text-sm ${
                  age === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {AGE_META[k].label}
              </button>
            ))}
          </div>

          <p className="mb-6 text-sm uppercase tracking-[0.22em] text-muted-foreground">{AGE_META[age].sub}</p>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {ACTIVITIES[age].map((a, i) => (
              <article key={a.title} className="rounded-2xl border border-border bg-background p-6">
                <span className="font-display text-3xl text-clay/80">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-display text-xl text-foreground text-balance">{a.title}</h3>
                <p className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">Tarvikkeet</p>
                <p className="text-sm text-foreground">{a.materials}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">Mitä lapsi oppii</p>
                <p className="text-sm text-muted-foreground">{a.benefit}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT HOW-TO */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Käyttöoppaat</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              Näin käytät tuotteita turvallisesti
            </h2>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-cream p-8">
              <span className="text-xs uppercase tracking-[0.25em] text-clay">Oppimistorni</span>
              <h3 className="mt-3 font-display text-2xl text-foreground text-balance">Aloita 12 kk iästä</h3>
              <ol className="mt-5 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
                <li><strong className="text-foreground">1.</strong> Aseta torni tiskialtaan viereen, seinää vasten — ei keskelle keittiötä.</li>
                <li><strong className="text-foreground">2.</strong> Nosta lapsi torniin aluksi itse; opeta sitten kiipeäminen sivusta.</li>
                <li><strong className="text-foreground">3.</strong> Aloita lyhyillä hetkillä: kädet veteen, vihannesten pesu.</li>
                <li><strong className="text-foreground">4.</strong> Älä jätä lasta torniin yksin. Ole aina käden ulottuvilla.</li>
                <li><strong className="text-foreground">5.</strong> Säädä korkeus lapsen mukaan: kyynärpää tason tasolla.</li>
              </ol>
              <Link to="/oppimistorni-opas" className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay">
                Koko oppimistorniopas →
              </Link>
            </div>
            <div className="rounded-2xl border border-border bg-cream p-8">
              <span className="text-xs uppercase tracking-[0.25em] text-clay">Pikler-kolmio</span>
              <h3 className="mt-3 font-display text-2xl text-foreground text-balance">Aloita 6 kk iästä</h3>
              <ol className="mt-5 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
                <li><strong className="text-foreground">1.</strong> Aseta kolmio pehmeälle pinnalle — matolle tai lattiapatjalle.</li>
                <li><strong className="text-foreground">2.</strong> Älä koskaan nosta lasta kolmion päälle. Hän kiipeää itse.</li>
                <li><strong className="text-foreground">3.</strong> Aluksi lapsi vain koskettaa puita — se riittää.</li>
                <li><strong className="text-foreground">4.</strong> Lisää ramppi tai kaari, kun lapsi kiipeää itsevarmasti.</li>
                <li><strong className="text-foreground">5.</strong> Tarkista ruuvit kerran kuukaudessa.</li>
              </ol>
              <Link to="/montessori-koulu/pikler-kiipeilyteline-opas" className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay">
                Koko Pikler-opas →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARENT TIPS */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Vanhempien vinkit</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              Arjen rakenteet, jotka toimivat
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Rauhoittumisnurkka", d: "Pehmeä matto, pari kirjaa ja tyyny — paikka, johon lapsi voi mennä itse säädelläkseen tunteita. Ei rangaistus, vaan turva.", to: "/montessori-koulu/montessori-lattiapatja" as const },
              { t: "Päivärutiinit", d: "Sama järjestys aamulla, ruoka-ajoilla ja illalla. Ennakoitavuus vapauttaa lapsen energiaa oppimiseen.", to: "/montessori-koulu/lapsi-keittiossa-ikaopas" as const },
              { t: "Motorinen kehitys", d: "Matalat esteet, pehmeät laskeutumispinnat, ei aikatauluja. Vapaa liike on Piklerin ydin.", to: "/montessori-koulu/kiipeilykolmio-lapselle-ikaopas" as const },
              { t: "Itsenäisyys", d: "Yksi asia lapsen mittakaavaan viikossa: koukku, hylly, jakkara. 3 kuukaudessa koti on muuttunut.", to: "/montessori-koulu/montessori-kalusteet-kotiin" as const },
            ].map((c) => (
              <Link key={c.t} to={c.to} className="group rounded-2xl border border-border bg-background p-6 transition-all hover:-translate-y-1 hover:border-foreground">
                <h3 className="font-display text-xl text-foreground text-balance">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
                <span className="mt-4 inline-flex text-xs uppercase tracking-[0.18em] text-foreground group-hover:text-clay">Lue lisää →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Resurssit</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              Syvenny aiheeseen
            </h2>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {[
              { to: "/montessori-koulu" as const, t: "Kaikki artikkelit — Montessori-koulu" },
              { to: "/montessori-koulu/maria-montessori-elamakerta" as const, t: "Maria Montessorin elämäkerta" },
              { to: "/montessori-koulu/emmi-pikler-elamakerta" as const, t: "Emmi Piklerin elämäkerta" },
              { to: "/montessori-koulu/montessori-waldorf-reggio" as const, t: "Montessori vs. Waldorf vs. Reggio" },
              { to: "/historia" as const, t: "Pedagogiikan historia & aikajana" },
              { to: "/ukk" as const, t: "Usein kysytyt kysymykset" },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="flex items-center justify-between rounded-xl border border-border bg-background px-5 py-4 text-foreground transition-colors hover:border-foreground">
                <span className="text-sm md:text-base">{r.t}</span>
                <span className="text-muted-foreground">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-background">
        <div className="container-prose py-20 md:py-28 text-center">
          <h2 className="font-display text-3xl text-foreground md:text-5xl text-balance mx-auto max-w-2xl">
            Valmis rakentamaan kodin lapsen ehdoilla?
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/kauppa" className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm uppercase tracking-[0.2em] text-background hover:bg-foreground/85">
              Selaa mallistoa →
            </Link>
            <Link to="/oppimistorni" className="inline-flex items-center gap-2 rounded-full border border-border bg-cream px-6 py-3 text-sm uppercase tracking-[0.2em] text-foreground hover:border-foreground">
              Oppimistornit
            </Link>
            <Link to="/pikler-kolmio" className="inline-flex items-center gap-2 rounded-full border border-border bg-cream px-6 py-3 text-sm uppercase tracking-[0.2em] text-foreground hover:border-foreground">
              Pikler-kolmiot
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
